import { useEffect, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ProLayout } from '@ant-design/pro-components';
import { Button, Grid, Tooltip } from 'antd';
import { Fire, List, Question, SignOut } from '@phosphor-icons/react';
import { navGroups } from './navigation';
import { isSuperAdmin, useCurrentStaff, useSession } from '../auth/session';
import { tokens } from '../theme';

/**
 * Khung ngoài của Admin Web — dựng trên `ProLayout` (ADR-009, PL chốt 2026-08-28).
 *
 * <p>Trước đây khung này là `AppSidebar` tự viết 291 dòng, bám sát prototype. PL chốt Admin Web
 * thôi bám prototype để cả bộ khung nhất quán và trông chuyên nghiệp hơn. `ProLayout` lo sẵn:
 * thanh bên thu gọn được, chuyển sang ngăn kéo ở khổ nhỏ, đánh dấu mục đang mở theo đường dẫn,
 * và bố cục đồng nhất cho mọi trang — ba thứ trước đây phải tự viết và tự bảo trì.</p>
 *
 * <p>📌 <b>`PageHeader` và `PageBody` giữ NGUYÊN chữ ký.</b> 10 trang đang dùng chúng; đổi khung
 * ngoài mà không đụng hợp đồng đó là cách duy nhất để thay khung trong một lần mà không phải sửa
 * mười trang cùng lúc — mỗi trang chuyển sang `ProTable`/`ProForm` theo nhịp riêng.</p>
 */

interface PageHeaderProps {
  title: string;
  /** Dòng phụ dưới tiêu đề — ngày giờ cập nhật, đường dẫn phân cấp */
  subtitle?: ReactNode;
  /** Nút hành động bên phải thanh tiêu đề */
  actions?: ReactNode;
  /** Nút "Gợi ý" khi banner hint bị đóng (HintToggle). Đặt cố định bên phải, cạnh actions. */
  hint?: ReactNode;
}

/** Thanh tiêu đề trang: h1, dòng phụ, cụm hành động bên phải. */
export function PageHeader({ title, subtitle, actions, hint }: PageHeaderProps) {
  return (
    <header
      style={{
        // minHeight (không phải height cố định): trang nhẹ giữ nguyên chiều cao, trang có
        // nhiều nút hành động thì header cao thêm và xuống dòng thay vì bóp vỡ tiêu đề.
        minHeight: tokens.layout.topbarHeight,
        flex: 'none',
        background: tokens.color.surface,
        borderBottom: `1px solid ${tokens.color.line}`,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
        rowGap: 10,
        padding: '10px 20px',
        // Không cho header đẩy rộng hơn cột nội dung: control rộng (RangePicker, nhiều Select)
        // trên màn hẹp phải co/xuống dòng chứ không tràn ngang cả trang.
        maxWidth: '100%',
        minWidth: 0,
        overflowX: 'hidden',
      }}
    >
      {/* Tiêu đề không co lại: chật thì cụm nút hành động xuống dòng, không phải tiêu đề vỡ chữ. */}
      <h1 style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.015em', margin: 0, flexShrink: 0 }}>
        {title}
      </h1>
      {subtitle && (
        <div style={{ fontSize: 12, color: tokens.color.ink3, minWidth: 0, flexShrink: 1 }}>{subtitle}</div>
      )}
      {(hint || actions) && (
        // Cụm bên phải: nút "Gợi ý" (nếu banner đóng) rồi tới các nút hành động. Đẩy về phải, tự
        // xuống dòng và KHÔNG vượt quá bề rộng header — chỗ chặn tràn ngang cho MỌI trang.
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
            minWidth: 0,
            maxWidth: '100%',
          }}
        >
          {hint}
          {actions}
        </div>
      )}
    </header>
  );
}

/** Vùng nội dung cuộn được, dùng bên dưới PageHeader trong từng trang. */
export function PageBody({ children }: { children: ReactNode }) {
  return (
    <main style={{ flex: 1, overflowY: 'auto', padding: tokens.space.sectionGap }}>{children}</main>
  );
}

/** Chữ viết tắt trên avatar: hai từ cuối của tên ("Bùi Khánh An" → "KA"). */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(-2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * `navGroups` → cây menu của ProLayout.
 *
 * <p>Bốn nhóm thành bốn `type: 'group'`. Mục đã hoãn khỏi Phase 1 (`heldReason`) giữ nguyên cách
 * xử cũ: <b>vẫn hiện nhưng mờ và không bấm được</b>. Xóa hẳn thì người dùng thử trong đội tưởng
 * hỏng và đi báo lỗi; để bấm được thì họ vào một màn sẽ không bao giờ có nội dung.</p>
 */
function buildMenu(hq: boolean) {
  return navGroups.map((group) => ({
    path: `group-${group.label}`,
    name: group.label,
    type: 'group' as const,
    children: group.items
      .filter((item) => !item.superAdminOnly || hq)
      .map((item) => ({
        path: item.path,
        name: item.label,
        icon: <item.icon size={16} aria-hidden />,
        disabled: Boolean(item.heldReason),
        // Giữ lại để đối chiếu prototype khi cần tra ngược, dù Admin Web không còn bám nó.
        screenId: item.screenId,
        heldReason: item.heldReason,
      })),
  }));
}

export function AppLayout() {
  const location = useLocation();
  const staff = useCurrentStaff();
  const signOut = useSession((state) => state.signOut);
  const hq = isSuperAdmin(staff);
  // Hồ sơ 360 nằm dưới `/members/:id` — giữ mục «Thành viên» đang chọn.
  const menuPath = location.pathname.startsWith('/members/') ? '/members' : location.pathname;

  // Màn nhỏ (< md): sider thành drawer, cần một hamburger để mở. `=== false` để lần render đầu (chưa
  // resolve breakpoint) coi là màn rộng, tránh nháy.
  const isNarrow = Grid.useBreakpoint().md === false;
  const [collapsed, setCollapsed] = useState(false);
  // Desktop luôn mở đầy đủ; mobile mặc định đóng (drawer/rail thu lại) tới khi bấm hamburger. Chỉ
  // đồng bộ khi VƯỢT ngưỡng — không ép đóng lại khi người dùng đã tự mở trong cùng khổ màn.
  useEffect(() => {
    setCollapsed(isNarrow);
  }, [isNarrow]);

  return (
    <ProLayout
      layout="side"
      siderWidth={tokens.sidebar.width}
      fixSiderbar
      // ProLayout tự biến sider thành DRAWER khi mobile; `collapsed` điều khiển mở/đóng nó.
      collapsed={collapsed}
      onCollapse={setCollapsed}
      // Màn rộng: KHÔNG header (mỗi trang tự dựng `PageHeader`, tránh hai thanh tiêu đề). Màn nhỏ:
      // một header mảnh chỉ có hamburger + tên app để mở/đóng menu.
      headerRender={
        isNarrow
          ? () => (
              <div
                style={{
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '0 8px',
                  background: tokens.color.surface,
                  borderBottom: `1px solid ${tokens.color.line}`,
                }}
              >
                <Button
                  type="text"
                  aria-label={collapsed ? 'Mở menu' : 'Đóng menu'}
                  icon={<List size={20} aria-hidden />}
                  onClick={() => setCollapsed((c) => !c)}
                />
                <Fire size={18} weight="fill" aria-hidden style={{ color: tokens.sidebar.activeBg }} />
                <span style={{ fontWeight: 700, letterSpacing: '-0.01em' }}>Reski HQ</span>
                <Link
                  to="/ho-tro"
                  style={{
                    marginLeft: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    minHeight: 44,
                    padding: '0 10px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: tokens.color.green,
                  }}
                >
                  <Question size={16} aria-hidden />
                  Hỗ trợ
                </Link>
              </div>
            )
          : false
      }
      route={{ path: '/', children: buildMenu(hq) }}
      location={{ pathname: menuPath }}
      // ⚠️ CẦN prop này, không chỉ `type: 'group'` trong dữ liệu menu. Thiếu nó thì ProLayout dựng
      // bốn nhóm thành bốn submenu GẬP LẠI ĐƯỢC và mặc định đóng — tức 10 mục còn thấy 1, người dùng
      // phải bấm mở từng nhóm. Thanh bên cũ luôn hiện đủ 10 mục và đó là hành vi phải giữ.
      menu={{ type: 'group', defaultOpenAll: true }}
      token={{
        bgLayout: tokens.color.canvas,
        sider: {
          colorMenuBackground: tokens.sidebar.bg,
          colorTextMenu: tokens.sidebar.text,
          colorTextMenuSecondary: tokens.sidebar.textMuted,
          colorTextMenuTitle: '#fff',
          colorTextMenuItemHover: '#fff',
          colorTextMenuSelected: '#fff',
          colorBgMenuItemSelected: tokens.sidebar.activeBg,
          colorBgMenuItemHover: tokens.sidebar.hoverBg,
          colorTextMenuActive: '#fff',
          colorMenuItemDivider: tokens.sidebar.border,
          colorTextCollapsedButton: tokens.sidebar.textMuted,
          colorTextCollapsedButtonHover: '#fff',
          paddingInlineLayoutMenu: 10,
        },
      }}
      menuHeaderRender={(_logo, _title, props) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 2px' }}>
          <span
            style={{
              width: 32,
              height: 32,
              flex: 'none',
              borderRadius: tokens.radius.control,
              background: tokens.sidebar.activeBg,
              display: 'grid',
              placeItems: 'center',
              color: '#fff',
            }}
          >
            <Fire size={18} weight="fill" aria-hidden />
          </span>
          {!props?.collapsed && (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>
                Reski HQ
              </div>
              <div style={{ fontSize: 10.5, color: tokens.sidebar.textMuted, marginTop: 1 }}>
                Motives Vietnam
              </div>
            </div>
          )}
        </div>
      )}
      menuItemRender={(item, dom) => {
        // Mục đã hoãn: không bọc Link (bấm vào sẽ điều hướng tới màn rỗng), thay bằng chú thích
        // nói RÕ VÌ SAO — đó là câu trả lời tại chỗ cho "cái này đâu rồi".
        if (item.heldReason) {
          return (
            <Tooltip title={item.heldReason} placement="right">
              <span style={{ opacity: 0.45, cursor: 'not-allowed' }}>{dom}</span>
            </Tooltip>
          );
        }
        return <Link to={item.path ?? '/'}>{dom}</Link>;
      }}
      menuFooterRender={(props) =>
        staff ? (
          <div style={{ padding: '8px 4px 4px', borderTop: `1px solid ${tokens.sidebar.border}` }}>
            {/* Màn rộng không có header (tránh hai thanh tiêu đề). Nút hỗ trợ nằm cuối thanh bên;
                màn nhỏ thì nút cùng tên nằm trên header. */}
            <Tooltip title={props?.collapsed ? 'Hỗ trợ' : undefined} placement="right">
              <Link
                to="/ho-tro"
                aria-label="Hỗ trợ"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  minHeight: 44,
                  padding: '0 8px',
                  marginBottom: 4,
                  borderRadius: tokens.radius.control,
                  color: tokens.sidebar.text,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <Question size={16} aria-hidden />
                {!props?.collapsed && 'Hỗ trợ'}
              </Link>
            </Tooltip>
          <div
            style={{
              padding: '8px 0 4px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 32,
                height: 32,
                flex: 'none',
                borderRadius: tokens.radius.pill,
                background: 'rgba(255,255,255,.14)',
                display: 'grid',
                placeItems: 'center',
                fontSize: 12,
                fontWeight: 700,
                color: '#fff',
              }}
            >
              {initialsOf(staff.name)}
            </span>
            {!props?.collapsed && (
              <>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: '#fff' }}>{staff.name}</div>
                  <div style={{ fontSize: 10.5, color: tokens.sidebar.textMuted, marginTop: 1 }}>
                    {staff.role === 'super_admin' ? 'Super Admin' : 'Quản lý chi nhánh'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={signOut}
                  title="Đăng xuất"
                  aria-label="Đăng xuất"
                  style={{
                    width: 32,
                    height: 32,
                    flex: 'none',
                    display: 'grid',
                    placeItems: 'center',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: tokens.radius.control,
                    background: 'transparent',
                    color: tokens.sidebar.text,
                  }}
                >
                  <SignOut size={16} aria-hidden />
                </button>
              </>
            )}
          </div>
          </div>
        ) : null
      }
      // ProLayout bọc nội dung trong một khối có padding riêng; tắt đi vì mỗi trang đã tự lo
      // `PageHeader` (không padding) + `PageBody` (có padding).
      contentStyle={{
        padding: 0,
        // Trừ chiều cao header mảnh (48 + 1px viền) trên màn nhỏ để nội dung không tràn quá màn.
        height: isNarrow ? 'calc(100vh - 49px)' : '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Outlet />
    </ProLayout>
  );
}
